<div class="page">
  <header class="page-header">
    <h1 class="page-title">Committee Review</h1>
    <p class="page-subtitle">Review and manage submitted ideas through the approval process</p>
  </header>

  <div class="toolbar">
    <label class="assigned-filter">
      <input type="checkbox" [checked]="assignedToMe" (change)="toggleAssignedToMe()" />
      Assigned to me
    </label>
  </div>

  <div *ngIf="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>

  <div *ngIf="loading" class="loading-state">
    <app-loader label="Loading ideas"></app-loader>
  </div>

  <div *ngIf="!loading && !errorMessage" class="board-wrap">
    <div class="board">
      <section *ngFor="let col of columns" class="column" [attr.data-col]="col.id">
        <header class="column-header">
          <span class="column-title">{{ col.label }}</span>
          <span class="column-count">{{ countFor(col.id) }}</span>
        </header>
        <div class="column-body">
          <div
            class="column-dropzone"
            [class.is-read-only]="!isColumnDroppable(col.id)"
            [class.is-drop-target]="dropTargetColumn === col.id"
            (dragover)="isColumnDroppable(col.id) && onColumnDragOver($event, col.id)"
            (dragleave)="isColumnDroppable(col.id) && onColumnDragLeave(col.id)"
            (drop)="isColumnDroppable(col.id) && onColumnDrop($event, col.id)"
          >
          <div
            *ngFor="let idea of columnIdeas[col.id]"
            class="idea-card"
            role="button"
            tabindex="0"
            [attr.draggable]="isColumnDraggable(col.id)"
            [class.is-dragging]="dragIdeaId === idea.id"
            (dragstart)="onCardDragStart($event, idea, col.id)"
            (dragend)="onCardDragEnd()"
            (click)="!draggingIdea && openReviewModal(idea, col.id)"
            (keydown.enter)="!draggingIdea && openReviewModal(idea, col.id)"
            (keydown.space)="$event.preventDefault(); !draggingIdea && openReviewModal(idea, col.id)"
          >
            <h3 class="idea-title">{{ idea.title }}</h3>
            <div class="idea-meta">
              <span class="idea-author">{{ authorLabel(idea) }}</span>
              <span class="meta-dot" aria-hidden="true">·</span>
              <span class="idea-likes" [attr.title]="getUpvoteCount(idea) + ' upvotes'">
                {{ getUpvoteCount(idea) }}
                <span class="thumb-emoji" aria-hidden="true">👍</span>
              </span>
            </div>
            <div class="idea-tags">
              <span class="tag tag-category">{{ idea.category.name }}</span>
              <span *ngIf="getSecondaryPillText(idea)" class="tag tag-size">{{ getSecondaryPillText(idea) }}</span>
            </div>
          </div>
          <p *ngIf="countFor(col.id) === 0" class="column-empty">No ideas</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</div>

<div
  *ngIf="reviewModalOpen"
  class="modal-backdrop"
  role="dialog"
  aria-modal="true"
  aria-labelledby="review-modal-title"
  (click)="closeReviewModal()"
>
  <div class="modal-panel" (click)="$event.stopPropagation()">
    <button type="button" class="modal-close" (click)="closeReviewModal()" aria-label="Close">×</button>

    <div *ngIf="reviewLoading" class="modal-loading">Loading…</div>
    <div *ngIf="!reviewLoading && reviewError" class="modal-error" role="alert">{{ reviewError }}</div>

    <ng-container *ngIf="modalIdea as idea">
      <h2 id="review-modal-title" class="modal-title">{{ idea.title }}</h2>

      <section class="modal-section">
        <h3 class="modal-label">Summary</h3>
        <p class="modal-summary">{{ summaryText(idea) }}</p>
        <button type="button" class="btn-full-details" (click)="viewFullDetails()">View Full Details →</button>
      </section>

      <section *ngIf="!isDeliveredIdea" class="modal-section">
        <h3 class="modal-label">Scoring Matrix</h3>
        <div *ngFor="let row of scoreRows" class="score-row">
          <span class="score-name">{{ row.label }}</span>
          <div class="score-control">
            <input
              type="range"
              min="1"
              max="5"
              [name]="row.key"
              [(ngModel)]="scores[row.key]"
              class="score-slider"
            />
          </div>
          <span class="score-value">{{ scores[row.key] }}/5</span>
        </div>
      </section>

      <section *ngIf="!isDeliveredIdea" class="modal-section">
        <h3 class="modal-label">Assign owner</h3>
        <p *ngIf="assignableUsersError" class="modal-field-hint modal-field-error" role="alert">{{ assignableUsersError }}</p>
        <p *ngIf="assigneeSaveError" class="modal-field-hint modal-field-error" role="alert">{{ assigneeSaveError }}</p>
        <select
          class="modal-select"
          title="Assign an active user as the idea reviewer"
          [(ngModel)]="selectedAssigneeUserId"
          (ngModelChange)="onAssigneeUserPicked($event)"
          [disabled]="reviewLoading || savingAssignee || savingDecision || promotingToReview || !assignableUsers.length"
        >
          <option value="">Unassigned</option>
          <option *ngFor="let u of assignableUsers" [value]="u.userId">{{ assigneeOptionLabel(u) }}</option>
        </select>
        <p *ngIf="savingAssignee" class="modal-field-hint">Saving assignment…</p>
      </section>

      <section *ngIf="!isDeliveredIdea" class="modal-section">
        <h3 class="modal-label">Committee Notes (Internal)</h3>
        <textarea
          class="modal-textarea"
          rows="4"
          placeholder="Internal notes for committee members..."
          [(ngModel)]="committeeNotes"
          [disabled]="savingDecision || promotingToReview"
        ></textarea>
      </section>

      <section *ngIf="isDeliveredIdea" class="modal-section">
        <h3 class="modal-label">Delivered Attachments</h3>
        <p class="modal-field-hint">Preview and download the proof files attached to this delivered idea.</p>
        <div *ngIf="deliverableUploadError" class="decision-error" role="alert">{{ deliverableUploadError }}</div>
        <div class="deliverable-list" *ngIf="deliverableAttachments(idea).length; else noDeliverables">
          <button
            *ngFor="let attachment of deliverableAttachments(idea)"
            type="button"
            class="deliverable-item"
            (click)="downloadDeliverable(idea.id, attachment)"
          >
            <div class="deliverable-item-main">
              <span class="deliverable-name">{{ attachment.fileName }}</span>
              <span class="deliverable-badge">{{ isImageAttachment(attachment) ? 'Image' : 'File' }}</span>
            </div>
            <div class="deliverable-item-meta">
              {{ formatFileSize(attachment.fileSizeBytes) }} • {{ formatDate(attachment.uploadedAtUtc) }}
            </div>
          </button>
        </div>
        <ng-template #noDeliverables>
          <p class="modal-field-hint">No delivered attachments yet.</p>
        </ng-template>
      </section>

      <section *ngIf="showMoveToDeliveredSection" class="modal-section modal-workflow">
        <h3 class="modal-label">Move to Delivered</h3>
        <p class="workflow-hint">
          Upload proof before moving this approved idea into the final <strong>Delivered</strong> column.
        </p>
        <div *ngIf="deliveryProofError" class="decision-error" role="alert">{{ deliveryProofError }}</div>
        <div class="deliverable-upload-row">
          <input
            #deliveryProofInput
            type="file"
            class="deliverable-file-input"
            multiple
            (change)="onDeliveryProofSelected($event)"
            [disabled]="deliveringWithProof"
          />
        </div>
        <div *ngIf="deliveryProofFiles.length" class="delivery-proof-summary">
          <div class="delivery-proof-summary-title">{{ selectedDeliveryProofSummary() }}</div>
          <ul class="delivery-proof-list">
            <li *ngFor="let file of deliveryProofFiles; let index = index" class="delivery-proof-item">
              <span class="delivery-proof-file-meta">{{ file.name }} • {{ formatFileSize(file.size) }}</span>
              <button
                type="button"
                class="delivery-proof-remove"
                (click)="removeDeliveryProofFile(index)"
                [disabled]="deliveringWithProof"
                aria-label="Remove selected file"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                  <path d="M9 3h6l1 2h5v2H3V5h5l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"/>
                </svg>
              </button>
            </li>
          </ul>
        </div>
        <div class="delivery-proof-actions">
          <button
            type="button"
            class="btn-confirm-delivery"
            (click)="moveAcceptedToDelivered(deliveryProofInput)"
            [disabled]="!canMoveAcceptedToDelivered"
          >
            {{ deliveringWithProof ? 'Saving…' : 'Move to Delivered' }}
          </button>
        </div>
      </section>

      <section *ngIf="canSendToUnderReview" class="modal-section modal-workflow">
        <h3 class="modal-label">Workflow</h3>
        <p class="workflow-hint">
          From <strong>Submitted</strong> or <strong>Triage</strong>, move the idea into formal committee review.
        </p>
        <button
          type="button"
          class="btn-send-review"
          [disabled]="savingDecision || promotingToReview"
          (click)="promoteToUnderReview()"
        >
          {{ promotingToReview ? 'Saving…' : 'Send to Under Review' }}
        </button>
      </section>

      <section *ngIf="!isDeliveredIdea" class="modal-section">
        <h3 class="modal-label">Decision</h3>
        <p *ngIf="committeeDecisionHint" class="decision-hint">{{ committeeDecisionHint }}</p>
        <div *ngIf="decisionError" class="decision-error" role="alert">{{ decisionError }}</div>
        <div class="decision-grid">
          <button
            type="button"
            class="btn-decision btn-request"
            [disabled]="savingDecision || promotingToReview || !committeeCanDecide"
            (click)="submitDecision('needMoreInfo')"
          >
            {{ pendingDecision === 'needMoreInfo' && savingDecision ? 'Saving…' : 'Need more info' }}
          </button>
          <button
            type="button"
            class="btn-decision btn-accept"
            [disabled]="savingDecision || promotingToReview || !canApproveCurrentIdea"
            (click)="submitDecision('accept')"
          >
            {{ pendingDecision === 'accept' && savingDecision ? 'Saving…' : 'Approve' }}
          </button>
          <button
            type="button"
            class="btn-decision btn-park"
            [disabled]="savingDecision || promotingToReview || !committeeCanDecide"
            (click)="submitDecision('park')"
          >
            {{ pendingDecision === 'park' && savingDecision ? 'Saving…' : 'Park' }}
          </button>
          <button
            type="button"
            class="btn-decision btn-reject"
            [disabled]="savingDecision || promotingToReview || !committeeCanDecide"
            (click)="submitDecision('reject')"
          >
            {{ pendingDecision === 'reject' && savingDecision ? 'Saving…' : 'Reject' }}
          </button>
        </div>
      </section>
    </ng-container>
  </div>
</div>
